/* ──────────────────────────────────────────────────────────────────────────
 * Intervention Simulator — §6.
 *
 * The brief names eight scenarios, A through H, and states the purpose in
 * one line:
 *
 *   "The platform estimates operational consequences BEFORE the officer
 *    selects the intervention."
 *
 * Which is to say: this is the step that turns prediction into decision
 * intelligence. Everything upstream says what will happen. This says what
 * would happen instead, for each option, with the costs attached.
 *
 * ── THREE RULES THAT KEEP THIS HONEST ──
 *
 * 1. AN INTERVENTION IS A CHANGE TO THE MODEL'S INPUTS, NOT ITS OUTPUTS.
 *    Each scenario sets inflow/egress multipliers and re-runs the same
 *    deterministic model. Nothing computes a "predicted improvement"
 *    directly, because a number produced that way can be made to say
 *    anything.
 *
 * 2. PEOPLE ARE CONSERVED. Divert pilgrims off a corridor and they arrive
 *    somewhere else — modelled as a transfer, never a deletion. A simulator
 *    that lets a diversion remove people from the theatre shows every
 *    option as a free win, which is worse than no simulator at all.
 *
 * 3. EVERY OPTION HAS A LAG, A COST AND A SIDE EFFECT, AND ALL THREE ARE
 *    REPORTED. A PA advisory does not take effect instantly and does not
 *    reach everyone; holding buses strands people upstream; deploying
 *    police takes officers from somewhere else. Scenario A — do nothing —
 *    is always included and is sometimes the right answer.
 *
 * Everything is SIMULATED. Effect sizes are stated assumptions.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST } from "../data/provenance";
import { step, forecast } from "./liveModel";
import { ASSEMBLY_AREA_M2, bandForDensity } from "./crowdSafety";
import { assessMobilityChain } from "./mobilityChain";
import { buildTwin } from "../../twin/twin";

/* How far ahead an intervention is judged. Long enough for the slower
 * levers (a bus hold, a PA advisory) to have landed. */
export const EVALUATION_HORIZON_MIN = 45;

/* Density at which a crowd is at crush risk — the top of LoS band D.
 * Time spent above this is the measure an intervention is judged on when
 * the peak has already been reached. */
export const CRITICAL_DENSITY = 4;

/* ── Effect assumptions ────────────────────────────────────────────────
 * Collected here rather than buried in each scenario so an officer can
 * challenge the numbers in one place. Every one of these is judgement. */
export const EFFECT_ASSUMPTIONS = {
  /* Fraction of a crowd that actually complies with a broadcast advisory.
   * Deliberately low — announcements at a mela compete with noise, and
   * assuming high compliance is how a plan fails in the field. */
  PA_COMPLIANCE: 0.18,
  PA_LAG_MIN: 6,
  /* Regulating a gate does not stop arrivals; it meters them. */
  GATE_REGULATION_FACTOR: 0.55,
  GATE_LAG_MIN: 3,
  /* A diversion moves a share of arrivals to the receiving sector. */
  DIVERSION_SHARE: 0.35,
  DIVERSION_LAG_MIN: 8,
  /* A bus hold stops that channel entirely for its duration. */
  BUS_HOLD_MIN: 10,
  /* Opening a corridor adds egress; reconfiguring a barricade trades
   * throughput for control. */
  ALT_CORRIDOR_EGRESS_GAIN: 0.45,
  ALT_CORRIDOR_LAG_MIN: 12,
  BARRICADE_EGRESS_GAIN: 0.2,
  BARRICADE_INFLOW_FACTOR: 0.8,
  BARRICADE_LAG_MIN: 5,
  /* Police improve flow discipline; they do not create space. */
  POLICE_EGRESS_GAIN: 0.15,
  POLICE_LAG_MIN: 15,
};

const A = EFFECT_ASSUMPTIONS;

/* ── Scenario catalogue ────────────────────────────────────────────────
 * Each returns the levers it pulls, given a target sector and the twin.
 * `transferTo` names where diverted people go — required for any lever
 * that reduces inflow by moving rather than deterring. */
export const SCENARIOS = [
  {
    id: "A",
    key: "do_nothing",
    label: { en: "Do nothing", mr: "काहीही करू नका" },
    description: {
      en: "Continue current operations. The baseline every other option is measured against.",
      mr: "सध्याची कार्यवाही सुरू ठेवा.",
    },
    agency: null,
    reversible: true,
    lagMin: 0,
    cost: { en: "None", mr: "काहीही नाही" },
    levers: () => ({}),
    sideEffects: () => [],
  },
  {
    id: "B",
    key: "regulate_gate",
    label: { en: "Temporarily regulate entry gate", mr: "प्रवेशद्वार नियंत्रित करा" },
    description: {
      en: "Meter arrivals at the sector's entry gates rather than closing them. Holds people upstream in a managed queue.",
      mr: "प्रवेशद्वारावर आगमन नियंत्रित करा.",
    },
    agency: "POLICE",
    reversible: true,
    lagMin: A.GATE_LAG_MIN,
    cost: { en: "Queue forms outside the gate; upstream pressure rises.", mr: "द्वाराबाहेर रांग तयार होते." },
    levers: (target) => ({
      inflowMultipliers: { [target]: A.GATE_REGULATION_FACTOR },
    }),
    sideEffects: (target, twin) => {
      const gates = twin.byType("gate").filter((g) => g.sector === target);
      return [{
        kind: "upstream_queue",
        text: `Arrivals held outside ${gates.length || "the"} entry gate${gates.length === 1 ? "" : "s"}. The queue itself becomes a crowd that needs managing and is not modelled here.`,
      }];
    },
  },
  {
    id: "C",
    key: "divert_route",
    label: { en: "Divert pilgrims via alternate route", mr: "पर्यायी मार्गाने वळवा" },
    description: {
      en: "Route a share of approaching pilgrims to a different ghat. Relieves the target; loads the receiver.",
      mr: "काही यात्रेकरूंना दुसऱ्या घाटाकडे वळवा.",
    },
    agency: "POLICE",
    reversible: true,
    lagMin: A.DIVERSION_LAG_MIN,
    cost: { en: "Longer walk for diverted pilgrims; receiving sector gains load.", mr: "वळवलेल्यांना जास्त चालावे लागते." },
    levers: (target, twin, liveState) => {
      const receiver = pickReceiver(target, liveState);
      if (!receiver) return {};
      const src = liveState.sectors.find((s) => s.id === target);
      const moved = Math.round((src?.inflowPerMin ?? 0) * A.DIVERSION_SHARE);
      return {
        inflowMultipliers: { [target]: 1 - A.DIVERSION_SHARE },
        transferIn: { [receiver.id]: moved },
        _receiver: receiver.id,
      };
    },
    sideEffects: (target, twin, liveState) => {
      const receiver = pickReceiver(target, liveState);
      if (!receiver) {
        return [{ kind: "blocked", text: "No sector has spare capacity to receive a diversion. This option is not available." }];
      }
      return [{
        kind: "load_transfer",
        text: `${Math.round(A.DIVERSION_SHARE * 100)}% of approaching pilgrims are moved to ${receiver.name?.en ?? receiver.id}, which gains that load. People are conserved, not removed.`,
        receiver: receiver.id,
      }];
    },
  },
  {
    id: "D",
    key: "hold_transport",
    label: { en: "Hold incoming buses", mr: "येणाऱ्या बस थांबवा" },
    description: {
      en: `Hold bus dispatch for ${A.BUS_HOLD_MIN} minutes. Cuts a supply channel upstream of the whole chain.`,
      mr: `बस प्रस्थान ${A.BUS_HOLD_MIN} मिनिटे थांबवा.`,
    },
    agency: "TRANSPORT",
    reversible: true,
    lagMin: 0,
    cost: { en: "Passengers accumulate at the terminal; the backlog arrives later as a sharper peak.", mr: "स्थानकावर गर्दी वाढते." },
    levers: () => ({ mobilityDisruption: { bus: 0 } }),
    sideEffects: () => [{
      kind: "deferred_surge",
      text: `Held passengers do not disappear. Expect the deferred cohort to arrive as a sharper peak after the hold is released — the mobility chain shows where and when.`,
    }],
  },
  {
    id: "E",
    key: "open_corridor",
    label: { en: "Open alternate corridor", mr: "पर्यायी मार्गिका उघडा" },
    description: {
      en: "Open a reserve corridor to increase the sector's egress capacity.",
      mr: "राखीव मार्गिका उघडून निर्गमन क्षमता वाढवा.",
    },
    agency: "POLICE",
    reversible: true,
    lagMin: A.ALT_CORRIDOR_LAG_MIN,
    cost: { en: "Requires staffing the new corridor and re-signing the route.", mr: "नवीन मार्गिकेसाठी कर्मचारी आवश्यक." },
    levers: (target) => ({
      egressMultipliers: { [target]: 1 + A.ALT_CORRIDOR_EGRESS_GAIN },
    }),
    sideEffects: (target, twin) => {
      const corridors = twin.byType("corridor").filter((c) => c.sector === target);
      return [{
        kind: "staffing",
        text: corridors.length
          ? `Opening a reserve corridor in ${target} requires officers drawn from elsewhere in the sector.`
          : `No reserve corridor is mapped in ${target}. The gain assumed here has no physical asset behind it in the twin.`,
        unsupported: corridors.length === 0,
      }];
    },
  },
  {
    id: "F",
    key: "change_barricade",
    label: { en: "Change barricade configuration", mr: "बॅरिकेड रचना बदला" },
    description: {
      en: "Reconfigure barricades to trade inflow for controlled outflow.",
      mr: "बॅरिकेड बदलून प्रवाह नियंत्रित करा.",
    },
    agency: "POLICE",
    reversible: true,
    lagMin: A.BARRICADE_LAG_MIN,
    cost: { en: "Physical work in a live crowd; briefly reduces both flows during the change.", mr: "गर्दीत प्रत्यक्ष काम करावे लागते." },
    levers: (target) => ({
      inflowMultipliers: { [target]: A.BARRICADE_INFLOW_FACTOR },
      egressMultipliers: { [target]: 1 + A.BARRICADE_EGRESS_GAIN },
    }),
    sideEffects: (target, twin) => {
      const bars = twin.byType("barricade").filter((b) => b.sector === target && b.adjustable);
      return [{
        kind: "field_work",
        text: bars.length
          ? `${bars.length} adjustable barricade${bars.length === 1 ? "" : "s"} in ${target}. Reconfiguration happens inside a live crowd and briefly reduces both flows.`
          : `No adjustable barricade is mapped in ${target}. This option cannot be executed as modelled.`,
        unsupported: bars.length === 0,
      }];
    },
  },
  {
    id: "G",
    key: "deploy_police",
    label: { en: "Deploy additional police", mr: "अतिरिक्त पोलीस तैनात करा" },
    description: {
      en: "Move a quick-reaction team into the sector to improve flow discipline.",
      mr: "क्यूआरटी पथक तैनात करा.",
    },
    agency: "POLICE",
    reversible: true,
    lagMin: A.POLICE_LAG_MIN,
    cost: { en: "Officers are taken from another sector, which loses that cover.", mr: "इतर सेक्टरमधून कर्मचारी हलवावे लागतात." },
    levers: (target) => ({
      egressMultipliers: { [target]: 1 + A.POLICE_EGRESS_GAIN },
    }),
    sideEffects: (target, twin) => {
      const donor = twin.byType("officer")
        .filter((u) => u.sector !== target && u.status === "available")
        .sort((a, b) => (b.strength ?? 0) - (a.strength ?? 0))[0];
      return [{
        kind: "cover_loss",
        text: donor
          ? `${donor.name?.en ?? donor.id} would be drawn from ${donor.sector}, which loses that cover for the duration.`
          : "No uncommitted unit is available. This option requires mutual aid from outside the theatre.",
        unsupported: !donor,
        donor: donor?.id ?? null,
      }];
    },
  },
  {
    id: "H",
    key: "public_advisory",
    label: { en: "Issue multilingual PA / VMS advisory", mr: "बहुभाषिक सूचना प्रसारित करा" },
    description: {
      en: "Broadcast a crowd advisory over PA arrays and variable message signs addressing the sector.",
      mr: "ध्वनिक्षेपक आणि फलकांवर सूचना द्या.",
    },
    agency: "COMMS",
    reversible: true,
    lagMin: A.PA_LAG_MIN,
    cost: { en: "Low cost, low and uncertain compliance. Repeated advisories lose effect.", mr: "कमी खर्च, अनिश्चित परिणाम." },
    levers: (target) => ({
      inflowMultipliers: { [target]: 1 - A.PA_COMPLIANCE },
    }),
    sideEffects: (target, twin) => {
      const pa = twin.byType("paSystem").filter((p) => p.addresses?.includes(target));
      const vms = twin.byType("vms").filter((v) => v.addresses?.includes(target));
      const langs = [...new Set([...pa, ...vms].flatMap((d) => d.languages ?? []))];
      return [{
        kind: "reach",
        text: pa.length || vms.length
          ? `${pa.length} PA array${pa.length === 1 ? "" : "s"} and ${vms.length} sign${vms.length === 1 ? "" : "s"} address ${target} in ${langs.join(", ")}. Assumed compliance is only ${Math.round(A.PA_COMPLIANCE * 100)}% — an advisory is the weakest lever available.`
          : `No PA or VMS asset addresses ${target}. This advisory cannot be delivered.`,
        unsupported: pa.length === 0 && vms.length === 0,
        languages: langs,
      }];
    },
  },
];

/* Pick the sector best able to absorb a diversion: lowest density with
 * genuine headroom. Returns null when nobody can take it, which makes
 * scenario C unavailable rather than silently harmful. */
function pickReceiver(targetId, liveState) {
  return liveState.sectors
    .filter((s) => s.id !== targetId)
    .map((s) => {
      const area = ASSEMBLY_AREA_M2[s.id];
      return { ...s, density: area ? s.current / area : null };
    })
    .filter((s) => s.density != null && s.density < 1.5 && s.current < s.capacity * 0.7)
    .sort((a, b) => a.density - b.density)[0] ?? null;
}

/* ── Evaluation ────────────────────────────────────────────────────────
 * Run the model forward under each scenario's levers and compare with A. */
function runScenario(liveState, levers, horizon) {
  const opts = {
    inflowMultipliers: levers.inflowMultipliers ?? null,
    egressMultipliers: levers.egressMultipliers ?? null,
    transferIn: levers.transferIn ?? null,
  };
  const at = forecast(liveState, [horizon], opts)[horizon];
  /* Peak matters as much as endpoint: an intervention that lands after the
   * crowd has already crushed is not a success. */
  let cursor = { ...liveState, sectors: liveState.sectors.map((s) => ({ ...s })) };
  const peak = {};
  /* Minutes spent above the critical density. This is the measure that
   * matters when a sector is ALREADY saturated: the peak cannot be
   * reduced because it has happened, but the time a crowd spends at
   * crush-risk density very much can be, and that is what an intervention
   * is for. Judging only on peak made every option in a saturated sector
   * report "does not help", which is both wrong and the case where help
   * is most needed. */
  const minutesAboveCritical = {};
  for (let i = 0; i < horizon; i += 1) {
    cursor = step(cursor, opts);
    cursor.sectors.forEach((s) => {
      const area = ASSEMBLY_AREA_M2[s.id] || 30000;
      const d = s.current / area;
      if (!peak[s.id] || d > peak[s.id]) peak[s.id] = d;
      if (d >= CRITICAL_DENSITY) {
        minutesAboveCritical[s.id] = (minutesAboveCritical[s.id] ?? 0) + 1;
      }
    });
  }
  return { at, peak, minutesAboveCritical };
}

export function simulateInterventions(liveState, targetSector, {
  horizon = EVALUATION_HORIZON_MIN, twin = null,
} = {}) {
  const t = twin ?? buildTwin(liveState);
  const baselineLevers = {};
  const baseline = runScenario(liveState, baselineLevers, horizon);

  const baseRow = (id) => baseline.at.find((r) => r.id === id);

  const options = SCENARIOS.map((sc) => {
    const levers = sc.levers(targetSector, t, liveState) ?? {};
    const sideEffects = sc.sideEffects(targetSector, t, liveState) ?? [];
    const unsupported = sideEffects.some((e) => e.unsupported);

    /* A lever with no physical asset behind it is reported as unavailable
     * rather than credited with the effect it cannot produce. */
    const result = unsupported
      ? null
      : runScenario(liveState, levers, horizon);

    /* Scenario D acts on the mobility chain rather than sector flows, so
     * its consequence is read from there. */
    const mobility = levers.mobilityDisruption
      ? assessMobilityChain(liveState, { disruptions: levers.mobilityDisruption, twin: t })
      : null;

    const target = result?.at.find((r) => r.id === targetSector) ?? null;
    const base = baseRow(targetSector);

    const deltaOccupancy = target && base ? target.occupancyPct - base.occupancyPct : null;
    const deltaDensity = target && base
      ? Number((target.density - base.density).toFixed(2)) : null;
    const peakDensity = result ? Number((result.peak[targetSector] ?? 0).toFixed(2)) : null;
    const basePeak = Number((baseline.peak[targetSector] ?? 0).toFixed(2));
    const deltaPeak = peakDensity == null ? null : Number((peakDensity - basePeak).toFixed(2));

    const baseCritical = baseline.minutesAboveCritical[targetSector] ?? 0;
    const optCritical = result ? (result.minutesAboveCritical[targetSector] ?? 0) : null;
    const deltaCriticalMinutes = optCritical == null ? null : optCritical - baseCritical;

    /* End-state density matters too: an option that does not cut the peak
     * but leaves the sector materially emptier at the horizon has bought
     * the commander room. */
    const deltaEndDensity = target && base
      ? Number((target.density - base.density).toFixed(2)) : null;

    /* Displacement: what this option does to every OTHER sector. The number
     * that stops a diversion looking free. */
    const displacement = result
      ? result.at
          .filter((r) => r.id !== targetSector)
          .map((r) => {
            const b = baseRow(r.id);
            return { id: r.id, name: r.name, deltaOccupancyPct: r.occupancyPct - b.occupancyPct };
          })
          .filter((d) => Math.abs(d.deltaOccupancyPct) >= 1)
          .sort((a, b) => b.deltaOccupancyPct - a.deltaOccupancyPct)
      : [];

    return {
      id: sc.id,
      key: sc.key,
      label: sc.label,
      description: sc.description,
      agency: sc.agency,
      reversible: sc.reversible,
      lagMin: sc.lagMin,
      cost: sc.cost,
      available: !unsupported,
      unavailableReason: unsupported
        ? sideEffects.find((e) => e.unsupported)?.text ?? "Required asset is not present in the twin."
        : null,
      /* Effect on the target sector at the horizon. */
      projectedOccupancyPct: target?.occupancyPct ?? null,
      projectedDensity: target?.density ?? null,
      projectedBand: target?.band ?? null,
      baselineBand: base?.band ?? null,
      bandChanged: target && base ? target.band !== base.band : null,
      deltaOccupancyPct: deltaOccupancy,
      deltaDensity,
      peakDensity,
      deltaPeakDensity: deltaPeak,
      minutesAboveCritical: optCritical,
      baselineMinutesAboveCritical: baseCritical,
      deltaCriticalMinutes,
      /* The honest summary: does it help, and does the help arrive in
       * time. Three ways to help — cut the peak, cut the time spent at
       * crush density, or leave the sector emptier at the horizon. A
       * saturated sector can only be helped by the second and third. */
      helps: result == null ? null : (
        (deltaPeak != null && deltaPeak < -0.05)
        || (deltaCriticalMinutes != null && deltaCriticalMinutes < 0)
        || (deltaEndDensity != null && deltaEndDensity < -0.05)
      ),
      helpKind: result == null ? null
        : (deltaPeak != null && deltaPeak < -0.05) ? "lower_peak"
        : (deltaCriticalMinutes != null && deltaCriticalMinutes < 0) ? "less_time_at_crush_density"
        : (deltaEndDensity != null && deltaEndDensity < -0.05) ? "emptier_at_horizon"
        : null,
      effectiveBeforeHorizon: sc.lagMin < horizon,
      displacement,
      sideEffects,
      mobility: mobility ? {
        binding: mobility.binding?.stage ?? null,
        chainThroughputPerMin: mobility.chainThroughputPerMin,
        propagation: mobility.propagation.slice(0, 3),
      } : null,
      trust: TRUST.SIMULATED,
    };
  });

  /* Rank: available options that help, by how much they cut the peak,
   * with lag as the tiebreak. "Do nothing" always stays on the list. */
  const ranked = [...options].sort((a, b) => {
    if (a.id === "A") return 1;
    if (b.id === "A") return -1;
    if (a.available !== b.available) return a.available ? -1 : 1;
    /* Rank on time spent at crush density first — it is the measure most
     * directly tied to harm — then on peak, then on lag. */
    const ca = a.deltaCriticalMinutes ?? 0;
    const cb = b.deltaCriticalMinutes ?? 0;
    if (ca !== cb) return ca - cb;
    const da = a.deltaPeakDensity ?? 0;
    const db = b.deltaPeakDensity ?? 0;
    if (da !== db) return da - db;
    return a.lagMin - b.lagMin;
  });

  const best = ranked.find((o) => o.available && o.helps) ?? null;

  return {
    targetSector,
    horizon,
    baselineBand: baseRow(targetSector)?.band ?? null,
    baselinePeakDensity: Number((baseline.peak[targetSector] ?? 0).toFixed(2)),
    options,
    ranked,
    recommended: best,
    /* Stated plainly, because a simulator that cannot say "none of these
     * help" is a sales tool. */
    noOptionHelps: !best,
    trust: TRUST.SIMULATED,
  };
}

export const INTERVENTION_ASSUMPTIONS = [
  "Each option is simulated by changing the live model's INPUTS and re-running it, not by computing an improvement directly. The comparison is like for like with the do-nothing baseline.",
  `Effect sizes are stated judgement: gate regulation meters arrivals to ${Math.round(A.GATE_REGULATION_FACTOR * 100)}%, an opened corridor adds ${Math.round(A.ALT_CORRIDOR_EGRESS_GAIN * 100)}% egress, additional police add ${Math.round(A.POLICE_EGRESS_GAIN * 100)}%. None are measured.`,
  `Public advisory compliance is assumed at only ${Math.round(A.PA_COMPLIANCE * 100)}%. Announcements at a mela compete with noise, and assuming high compliance is how a plan fails in the field.`,
  "People are conserved. A diversion moves load to a named receiving sector and the displacement is reported. Options are never credited with removing people from the theatre.",
  "An option whose physical asset is absent from the twin — no adjustable barricade, no PA array addressing the sector — is marked unavailable rather than credited with an effect it cannot produce.",
  "Lag is modelled but the transition is not: the simulator compares the end state, and does not represent the disruption caused while a barricade is being moved in a live crowd.",
  `An option helps if it lowers the projected peak, reduces the minutes spent above ${CRITICAL_DENSITY} persons/m², or leaves the sector emptier at the horizon. Judging on peak alone reports "no option helps" in an already-saturated sector, which is exactly where help matters most.`,
  "Scenario A (do nothing) is always evaluated and is sometimes the correct answer. If no option improves any of the three measures, the simulator says so.",
];
